import React from 'react';
import { motion } from 'framer-motion';
import { Search, FileText, ArrowRight } from 'lucide-react';

export default function SearchResults({ query, results, loading, error, onResultClick }) {
  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg pt-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4"></div>
              <p className="text-gray-400">Recherche en cours...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-primary-bg pt-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-6 mb-6">
            <div className="text-center">
              <p className="text-red-400">Erreur lors de la recherche: {error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!query) {
    return (
      <div className="min-h-screen bg-primary-bg pt-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Recherche</h1>
            <p className="text-gray-400">Entrez un terme de recherche pour trouver des agents, conversations, produits et plus...</p>
          </div>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="min-h-screen bg-primary-bg pt-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Aucun résultat</h1>
            <p className="text-gray-400 mb-4">Aucun résultat trouvé pour "{query}"</p>
            <p className="text-gray-500 text-sm">Essayez avec des mots-clés différents ou vérifiez l'orthographe</p>
          </div>
        </div>
      </div>
    );
  }

  // Grouper les résultats par type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  const typeLabels = {
    agent: 'Agents IA',
    conversation: 'Conversations',
    product: 'Produits',
    order: 'Commandes',
    knowledge: 'Base de connaissances'
  };

  const typeIcons = {
    agent: '🤖',
    conversation: '💬',
    product: '📦',
    order: '🛒',
    knowledge: '📚'
  };

  const typeColors = {
    agent: 'text-accent-green',
    conversation: 'text-blue-400',
    product: 'text-purple-400',
    order: 'text-orange-400',
    knowledge: 'text-pink-400'
  };

  return (
    <div className="min-h-screen bg-primary-bg pt-24">
      <div className="max-w-4xl mx-auto px-6">
        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Résultats pour "{query}"
          </h1>
          <p className="text-gray-400">
            {results.length} résultat{results.length > 1 ? 's' : ''} trouvé{results.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Résultats groupés par type */}
        <div className="space-y-8">
          {Object.entries(groupedResults).map(([type, typeResults]) => (
            <div key={type} className="bg-primary-card rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">{typeIcons[type]}</span>
                <h2 className="text-xl font-semibold text-white">
                  {typeLabels[type] || type}
                </h2>
                <span className="text-gray-400 text-sm">({typeResults.length})</span>
              </div>

              <div className="space-y-3">
                {typeResults.map((result, index) => (
                  <motion.div
                    key={`${result.type}-${result.id}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => onResultClick(result)}
                    className="bg-primary-dark/30 rounded-lg p-4 border border-primary-dark/50 hover:border-accent-green/30 cursor-pointer transition-all hover:shadow-lg hover:shadow-accent-green/10"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium mb-1 truncate">
                          {result.title || result.name || result.subject || 'Sans titre'}
                        </h3>
                        <p className="text-gray-400 text-sm mb-2 line-clamp-2">
                          {result.description || result.content || result.summary || 'Aucune description'}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          {result.createdAt && (
                            <span>Créé {new Date(result.createdAt).toLocaleDateString()}</span>
                          )}
                          {result.status && (
                            <span className={`px-2 py-1 rounded-full ${typeColors[type]} bg-current/20`}>
                              {result.status}
                            </span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className={`w-5 h-5 ${typeColors[type]} flex-shrink-0 mt-1`} />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
